import {getBillingMode} from 'ApplicationEntry/ducks/qualityBillingAgent';
import {PlanCodeItem, ValidPlanCodes} from 'ApplicationEntry/models/PlanCodes';
import {Premium, ValidPremium} from 'ApplicationEntry/models/Premium';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';
import {Product} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {NonEmptyString} from 'Core/models/EmptyString';

export const getSelectedPolicy = (policy: object, availableProducts: Product[]) => {
  const policyTypesSelected: Product = policy;

  const lowerCaseProductGroup = policy['productGroup'].toLowerCase();
  switch (lowerCaseProductGroup) {
    case ProductRiderGroups[ProductsRidersPolicyType.CriticalIllness].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Cancer].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Disability].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Shop].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Icrtn].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Iwl].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.HeartStroke].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Accident].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.UniversalLife].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.GLTerm].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.PinCare].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.AccidentNewYork].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Gwl].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.CriticalIllnessNY].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.DitWuNY].toLowerCase():
    case ProductRiderGroups[ProductsRidersPolicyType.Aip].toLowerCase():
      return availableProducts.find(product => product.productGroup?.toLowerCase() === lowerCaseProductGroup)!;
    default:
      return {};
  }

  return policyTypesSelected;
};

type riderType = {
  units: number;
  code: string;
  tracInput: string;
  classBase: string;
  phase: string;
  description: string;
  planLevel: string;
  tracInputLife70: string;
  onTheJobRiderBenefit: number;
};

export const getPlanCodes = (policy: object) => {
  const defaultItem: PlanCodeItem = {
    phase: policy['basePlanPhase'],
    planCode: policy['tracInput'],
    description: policy['basePlanDescription'],
    units: String(policy['planCode']) === 'GUL23' ? Number(policy['faceAmount']) / 1000 : policy['baseUnits'],
    planLevel: '',
    tracInputL70: policy['tracInputLife70'],
    onTheJobRiderBenefit: policy['disabilityMonthlyBenefit'],
  };
  const planCodeItems: PlanCodeItem[] = [defaultItem];
  const riders: riderType[] = policy['riders'];

  riders.forEach(rider => {
    if (rider.tracInput !== 'CILNIIRV') {
      const item: PlanCodeItem = {
        phase: rider.phase,
        planCode: rider.tracInput,
        description: rider.description,
        units: rider.units,
        planLevel: rider.planLevel,
        tracInputL70: rider.tracInputLife70,
        onTheJobRiderBenefit: rider.onTheJobRiderBenefit,
      };
      planCodeItems.push(item);
    }
  });

  const coverageLevel = policy['coverageLevel'];
  const packageOption = policy['planOption'];
  const productType = policy['planCode'];
  const issueType = policy['issueType'];
  const faceAmount = policy['faceAmount'];
  const monthlySalary = policy['monthlySalary'];
  const monthlyBenefit = policy['monthlyBenefit'];
  const planOption = policy['planOption'];
  return ValidPlanCodes.create(
    planCodeItems,
    NonEmptyString.create(coverageLevel),
    NonEmptyString.create(packageOption),
    NonEmptyString.create(productType),
    false,
    NonEmptyString.create(issueType),
    NonEmptyString.create(faceAmount),
    NonEmptyString.create(monthlySalary),
    NonEmptyString.create(monthlyBenefit),
    NonEmptyString.create(planOption)
  );
};

export const getPremiumDetails = (
  policy: object,
  numberOfPays: number,
  billing: string,
  replacement: boolean,
  cashWithApplication: object,
  totalPremium: number
) => {
  let monthly = 0;
  let weekly = 0;
  let biweekly = 0;
  let semimonthly = 0;
  let semiannual = 0;
  let quarterly = 0;
  let ninthly = 0;
  let tenthly = 0;
  let thirteenthly = 0;
  let annually = 0;
  let twentyeightdays = 0;

  const section125Value = Boolean(policy['section125']);
  const totalPrem = totalPremium;
  const deathBenefit = policy['deathBenefit'];
  const replacementPremium = policy['replacementPremium'];
  const eeiIndicator = policy['eeiIndicator'];
  const billingMode = getBillingMode(billing);

  if (numberOfPays === 12) {
    monthly = parseFloat((totalPrem / 12).toFixed(2));
    const annualPremium = totalPrem;

    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 52) {
    weekly = parseFloat((totalPrem / 52).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 26) {
    biweekly = parseFloat((totalPrem / 26).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 4) {
    quarterly = parseFloat((totalPrem / 4).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 13) {
    twentyeightdays = parseFloat((totalPrem / 13).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 24) {
    semimonthly = parseFloat((totalPrem / 24).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 2) {
    semiannual = parseFloat((totalPrem / 2).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 1) {
    annually = parseFloat(totalPrem.toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 9) {
    ninthly = parseFloat((totalPrem / 9).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 10) {
    tenthly = parseFloat((totalPrem / 10).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    thirteenthly = parseFloat((annualPremium / 13).toFixed(2));
  } else if (numberOfPays === 13) {
    thirteenthly = parseFloat((totalPrem / 13).toFixed(2));
    const annualPremium = totalPrem;

    monthly = parseFloat((annualPremium / 12).toFixed(2));
    weekly = parseFloat((annualPremium / 52).toFixed(2));
    biweekly = parseFloat((annualPremium / 26).toFixed(2));
    quarterly = parseFloat((annualPremium / 4).toFixed(2));
    twentyeightdays = parseFloat((annualPremium / 13).toFixed(2));
    semimonthly = parseFloat((annualPremium / 24).toFixed(2));
    semiannual = parseFloat((annualPremium / 2).toFixed(2));
    annually = parseFloat(annualPremium.toFixed(2));
    ninthly = parseFloat((annualPremium / 9).toFixed(2));
    tenthly = parseFloat((annualPremium / 10).toFixed(2));
  }
  let premiumDetails: Premium;

  premiumDetails = ValidPremium.create(
    NonEmptyString.create(weekly.toFixed(2)),
    NonEmptyString.create(biweekly.toFixed(2)),
    NonEmptyString.create(twentyeightdays.toFixed(2)),
    NonEmptyString.create(semimonthly.toFixed(2)),
    NonEmptyString.create(monthly.toFixed(2)),
    NonEmptyString.create(quarterly.toFixed(2)),
    NonEmptyString.create(semiannual.toFixed(2)),
    NonEmptyString.create(ninthly.toFixed(2)),
    NonEmptyString.create(tenthly.toFixed(2)),
    NonEmptyString.create(thirteenthly.toFixed(2)),
    NonEmptyString.create(annually.toFixed(2)),
    NonEmptyString.create(Number(cashWithApplication['amount']).toFixed(2)),
    NonEmptyString.create(billingMode),
    NonEmptyString.create(''),
    section125Value,
    deathBenefit,
    replacementPremium,
    eeiIndicator,
    replacement,
    false
  );

  return premiumDetails;
};
